<template>
  <div class="d-inline-flex align-center" v-if="$can('create', 'Address')">
    <slot name="activator" :on="{ click: open }">
      <v-btn 
        v-if="action == 'create' && !customActivator" 
        color="primary" 
        outlined 
        depressed 
        small 
        @click="open"
      >
        <v-icon x-small class="mr-2">fas fa-plus</v-icon>
        Agregar Dirección
      </v-btn>
    </slot>
    
    <v-btn 
      v-if="action == 'edit'" 
      icon 
      x-small 
      color="primary" 
      @click="actionEdit"
      class="mx-1"
    >
      <v-icon x-small>fas fa-edit</v-icon>
    </v-btn>

    <v-dialog v-model="showDialog" max-width="500" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet color="grey lighten-4" class="px-6 py-4 border-bottom">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-3">
              {{ action == 'create' ? 'fas fa-map-marked-alt' : 'fas fa-map-marker-alt' }}
            </v-icon>
            <div>
              <div class="text-h6 font-weight-bold lh-1">
                {{ action == 'create' ? 'Crear Dirección' : 'Editar Dirección' }}
              </div>
              <div class="caption grey--text mt-1">
                {{ action == 'create' ? 'Registrar nuevo punto de entrega' : 'Actualizar datos de ubicación' }}
              </div>
            </div>
          </div>
        </v-sheet>

        <v-card-text class="pa-6 pt-8">
          <ValidationObserver v-slot="{ handleSubmit }">
            <v-form @submit.prevent="handleSubmit(submit)">
              <v-row dense>
                <v-col cols="12">
                  <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="name"
                      label="Nombre / Etiqueta"
                      outlined
                      dense
                      placeholder="Ej: Bodega Central"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6">
                  <ValidationProvider :name="international.region_label" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="region"
                      :label="international.region_label"
                      outlined
                      dense
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6">
                  <ValidationProvider :name="international.comune_label" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="comune"
                      :label="international.comune_label"
                      outlined
                      dense
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <ValidationProvider name="Ciudad" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="city"
                      label="Ciudad"
                      outlined
                      dense
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="8">
                  <ValidationProvider name="Calle" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="street"
                      label="Calle"
                      outlined
                      dense
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="4">
                  <ValidationProvider name="Número" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="number"
                      label="Número"
                      outlined
                      dense
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="suite"
                    label="Referencia Adicional"
                    outlined
                    dense
                    placeholder="Piso, Depto, Oficina"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-divider class="my-6"></v-divider>

              <div class="d-flex justify-space-between align-center px-2">
                <div>
                  <v-btn 
                    v-if="action == 'edit'" 
                    text 
                    color="error" 
                    small 
                    @click="showConfirmDelete = true"
                    class="px-0"
                  >
                    <v-icon x-small class="mr-1">fas fa-trash-alt</v-icon>
                    Eliminar
                  </v-btn>
                </div>
                <div class="d-flex">
                  <v-btn text small @click="cancel()" class="mr-2" :disabled="loading">
                    Cancelar
                  </v-btn>
                  <v-btn 
                    type="submit" 
                    color="primary" 
                    depressed 
                    small 
                    class="px-6"
                    :loading="loading"
                  >
                    <v-icon x-small class="mr-2">fas fa-save</v-icon>
                    {{ action == 'create' ? 'Registrar' : 'Actualizar' }}
                  </v-btn>
                </div>
              </div>
            </v-form>
          </ValidationObserver>
        </v-card-text>
      </v-card>
    </v-dialog>

    <confirm
      v-model="showConfirmDelete" 
      message="¿Confirma eliminar esta dirección? Esta operación no se puede deshacer."
      @ok="deleteAddress"
      @cancel="showConfirmDelete = false"
    />
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import { createAddress, deleteAddress, updateAddress } from '@/api/customers'

export default {
  name: 'AddressCreateUpdate2',
  props: {
    customerId: {
      type: Number,
      required: false,
      default: null
    },
    value: {
      type: Object,
      default: null
    },
    action: {
      type: String,
      default: 'create'
    },
    customActivator: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    international() {
      return this.$store.getters.getInternational;
    }
  },
  data: () => ({
    name: '',
    region: '',
    comune: '',
    city: '',
    street: '',
    number: '',
    suite: '',
    showDialog: false,
    showConfirmDelete: false,
    loading: false
  }),
  methods: {
    open() {
      this.cancel();
      this.showDialog = true;
    },
    submit() {
      this.loading = true;
      if (this.action == 'create') this.addAddress();
      else if (this.action == 'edit') this.editAddress();
    },
    async addAddress() {
      if (!this.customerId) {
        notifications.showError('Error', 'ID de cliente no válido');
        this.loading = false;
        return;
      }
      let address = {
        name: this.name,
        region: this.region,
        comune: this.comune,
        city: this.city,
        street: this.street,
        number: this.number,
        suite: this.suite,
        default: false,
        customer: this.customerId
      }
      await createAddress(address)
      .then((data) => {
        notifications.showSuccess('Dirección creada correctamente');
        this.$emit('created', data || address);
        this.cancel();
      })
      .catch((e) => {
        notifications.showError('Error al crear la dirección: ' + e);
      })
      .finally(() => {
        this.loading = false;
      });
    },
    async editAddress() {
      let address = {
        id: this.value.id,
        name: this.name,
        region: this.region,
        comune: this.comune,
        city: this.city,
        street: this.street,
        number: this.number,
        suite: this.suite,
        default: false,
      }
      await updateAddress(address)
      .then((data) => {
        notifications.showSuccess('Dirección actualizada correctamente');
        this.$emit('updated', data || address);
        this.cancel();
      })
      .catch((e) => {
        notifications.showError('Error al actualizar la dirección: ' + e);
      })
      .finally(() => {
        this.loading = false;
      });
    },
    actionEdit() {
      this.name = this.value.name;
      this.region = this.value.region;
      this.comune = this.value.comune;
      this.city = this.value.city;
      this.street = this.value.street;
      this.number = this.value.number;
      this.suite = this.value.suite;
      this.showDialog = true;
    },
    async deleteAddress() {
      this.loading = true;
      await deleteAddress({ id: this.value.id })
      .then(() => {
        notifications.showSuccess('Dirección eliminada correctamente');
        this.$emit('deleted', this.value);
        this.cancel();
      })
      .catch((e) => {
        notifications.showError('Error al eliminar la dirección: ' + e);
      })
      .finally(() => {
        this.loading = false;
      });
    },
    cancel() {
      this.showDialog = false;
      this.showConfirmDelete = false;
      if (this.action == 'create') {
        this.name = '';
        this.region = '';
        this.comune = '';
        this.street = '';
        this.number = '';
        this.suite = '';
        this.city = '';
      }
      this.loading = false;
    }
  }
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
}
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.lh-1 { line-height: 1.2; }
</style>